/**
 * Inline media players shared by the admin Creative Library and the public
 * proposal share page (v2-1).
 *
 * - VideoPlayer: a native HTML5 <video controls> with preload="none" (lazy — the
 *   browser only fetches bytes once the operator/client hits play, per the
 *   ui-ux-pro-max performance guidance) and a portrait/landscape-aware frame.
 * - PdfPreview: opens the PDF in a new tab + an optional inline <iframe> preview.
 *
 * All sources are public-by-direct-link GCS URLs (no auth needed to play).
 *
 * SECURITY: item URLs are operator-pasted and can render on the PUBLIC share
 * page. Every src/href here is gated through isSafeHttpUrl() — a non-http(s) URL
 * (javascript:, data:, …) renders a disabled "unavailable" state instead of a
 * live link/iframe. The backend rejects such URLs too; this is defence-in-depth.
 */

import { useState } from 'react';

import { isSafeHttpUrl } from '../lib/safeUrl';

type Orientation = '16x9' | '9x16' | null | undefined;

function Unavailable({ label }: { label: string }) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-line bg-surface-muted px-3 py-2 text-sm text-ink-500"
      role="status"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M4.9 4.9 19 19" />
      </svg>
      This item is unavailable.
      <span className="sr-only"> — {label}</span>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

export function VideoPlayer({
  src,
  label,
  orientation,
  poster,
}: {
  src: string;
  label: string;
  orientation?: Orientation;
  /** Optional poster / thumbnail URL shown before the video plays. */
  poster?: string | null;
}) {
  const portrait = orientation === '9x16';
  if (!isSafeHttpUrl(src)) return <Unavailable label={label} />;
  const safePoster = poster && isSafeHttpUrl(poster) ? poster : undefined;
  return (
    <div
      className={[
        'mx-auto w-full overflow-hidden rounded-xl bg-ink-900',
        portrait ? 'max-w-[280px]' : 'max-w-full',
      ].join(' ')}
    >
      <video
        controls
        preload="none"
        playsInline
        {...(safePoster ? { poster: safePoster } : {})}
        className={['h-auto w-full bg-black', portrait ? 'aspect-[9/16]' : 'aspect-video'].join(' ')}
        aria-label={label}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support inline video.{' '}
        <a href={src} className="text-primary-300 underline">
          Open the video
        </a>
        .
      </video>
    </div>
  );
}

export function PdfPreview({
  src,
  label,
  withInlinePreview = false,
}: {
  src: string;
  label: string;
  withInlinePreview?: boolean;
}) {
  if (!isSafeHttpUrl(src)) return <Unavailable label={label} />;
  return (
    <div className="space-y-3">
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-sm font-semibold text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <PdfIcon />
        Open PDF
        <span className="sr-only"> — {label}</span>
      </a>
      {withInlinePreview && (
        <iframe
          src={src}
          title={`${label} preview`}
          className="h-[480px] w-full rounded-xl border border-line bg-white"
        />
      )}
    </div>
  );
}

/**
 * ImagePreview (v3-3): a generated hero image in the Creative Library.
 *
 * Renders the image inline (lazy-loaded) + a "Copy hero URL" button so the
 * operator can paste the public GCS URL into the Generate-PDF / Generate-Video
 * hero field. The src is gated through isSafeHttpUrl() like the other players.
 */
export function ImagePreview({ src, label }: { src: string; label: string }) {
  const [copied, setCopied] = useState(false);
  if (!isSafeHttpUrl(src)) return <Unavailable label={label} />;

  async function copy() {
    try {
      await navigator.clipboard.writeText(src);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-line bg-surface-muted">
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="h-auto w-full object-cover"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={copy}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {copied ? 'Copied!' : 'Copy hero URL'}
        </button>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium text-ink-500 underline hover:text-primary-700"
        >
          Open full size
        </a>
      </div>
    </div>
  );
}

/**
 * AudioPlayer (v3-4): a generated voiceover in the Creative Library.
 *
 * Renders a native HTML5 <audio controls> with preload="none" (lazy — the browser
 * only fetches bytes once the operator hits play, per the ui-ux-pro-max
 * performance guidance). The src is gated through isSafeHttpUrl() like the other
 * players; a non-http(s) URL renders the disabled "unavailable" state instead.
 */
export function AudioPlayer({ src, label }: { src: string; label: string }) {
  if (!isSafeHttpUrl(src)) return <Unavailable label={label} />;
  return (
    <div className="rounded-xl border border-line bg-surface-muted p-3">
      <audio
        controls
        preload="none"
        className="w-full"
        aria-label={label}
      >
        <source src={src} type="audio/mpeg" />
        Your browser does not support inline audio.{' '}
        <a href={src} className="text-primary-700 underline">
          Open the audio
        </a>
        .
      </audio>
    </div>
  );
}

export { PlayIcon };
